import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { lazy } from 'react';
import { ErrorFallbackContainer } from './components/_misc/layouts/error/ErrorFallbackContainer';
import { AppLoader } from './components/_misc/layouts/app-loader/AppLoader';
import { RootLayout } from './RootLayout';
import { ProviderWrapper } from './ProviderWrapper';

const AdminPanelContainer = lazy(() => import('./components/admin-panel/AdminPanelContainer'));
const BlogImportDashboard = lazy(() => import('./components/admin-panel/BlogImportDashboard'));
const AIBlogGenerationDashboard = lazy(() => import('./components/admin-panel/AIBlogGenerationDashboard'));
const FeaturedReferralPage = lazy(() => import('./components/admin-panel/FeaturedReferralPage'));
const ThemeShowcasePage = lazy(() => import('./components/admin-panel/ThemeShowcasePage'));
const CoverLetterGeneratorPage = lazy(() => import('./components/admin-panel/CoverLetterGeneratorPage'));
const AnalyticsDashboardContainer = lazy(() => import('./components/analytics/AnalyticsDashboardContainer'));
const BlogPostContainer = lazy(() => import('./components/blog-post/BlogPostContainer'));
const BlogPostsContainer = lazy(() => import('./components/blog-post/BlogPostsContainer'));
const ColorTheoryBlogPost = lazy(() => import('./components/blog-post/ColorTheoryBlogPost'));
const ResponsivenessBlogPost = lazy(() => import('./components/blog-post/ResponsivenessBlogPost'));
const AutomationBlogPost = lazy(() => import('./components/blog-post/AutomationBlogPost'));
const DiscoverContainer = lazy(() => import('./components/discover/DiscoverContainer'));
const ContactContainer = lazy(() => import('./components/contact/ContactContainer'));
const LoginContainer = lazy(() => import('./components/login/LoginContainer'));
const GroupReferralContainer = lazy(() => import('./components/group-referral/GroupReferralContainer'));
const PageContainer = lazy(() => import('./components/page/PageContainer'));
const ReferralContainer = lazy(() => import('./components/referral/ReferralContainer'));
const SearchContainer = lazy(() => import('./components/search/SearchContainer'));
const StoreContainer = lazy(() => import('./components/store/StoreContainer'));
const BlankStoreContainer = lazy(() => import('./components/store/BlankStoreContainer'));
const BGTesterContainer = lazy(() => import('./components/store/BGTesterContainer'));
const PaletteTester = lazy(() => import('./components/store/PaletteTester'));
const LandingContainer2 = lazy(() => import('./components/landing/LandingContainer2'));
const LandingContainer3 = lazy(() => import('./components/landing3/LandingContainer3'));
const FeedbackContainer = lazy(() => import('./components/feedback/FeedbackContainer'));
const TipContainer = lazy(() => import('./components/tip/TipContainer'));
const DiscountContainer = lazy(() => import('./components/discount/DiscountContainer'));
const UserContainer = lazy(() => import('./components/user/UserContainer'));
const VerifyContainer = lazy(() => import('./components/login/VerifyContainer'));
const SiteReferralContainer = lazy(() => import('./components/site-referral/SiteReferralContainer'));
const AboutContainer = lazy(() => import('./components/about/AboutContainer'));
const TermsContainer = lazy(() => import('./components/legal/TermsContainer'));
const PrivacyContainer = lazy(() => import('./components/legal/PrivacyContainer'));
const GroupReferralSlugContainer = lazy(() => import('./components/group-referral/slug/GroupReferralSlugContainer'));

const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    errorElement: (
      <ErrorFallbackContainer
        isNotFound
        title="Page Not Found..."
        subTitle="Sorry, we couldn't find the page you're looking for."
      />
    ),
    children: [
      {
        index: true,
        element: (
          <ProviderWrapper>
            <DiscoverContainer />
          </ProviderWrapper>
        ),
      },
      {
        path: 'join',
        element: (
          <ProviderWrapper>
            <LandingContainer3 />
          </ProviderWrapper>
        ),
      },
      {
        // Pre-redesign landing page, kept for reference/comparison
        path: '2',
        element: (
          <ProviderWrapper>
            <LandingContainer2 />
          </ProviderWrapper>
        ),
      },
      {
        path: 'login',
        element: (
          <ProviderWrapper>
            <LoginContainer />
          </ProviderWrapper>
        ),
      },
      {
        path: 'verify',
        element: (
          <ProviderWrapper>
            <VerifyContainer />
          </ProviderWrapper>
        ),
      },
      {
        path: 'admin-panel',
        element: (
          <ProviderWrapper>
            <AdminPanelContainer />
          </ProviderWrapper>
        ),
      },
      {
        path: 'admin-panel/blog/import',
        element: (
          <ProviderWrapper>
            <BlogImportDashboard />
          </ProviderWrapper>
        ),
      },
      {
        path: 'admin-panel/blog/generate',
        element: (
          <ProviderWrapper>
            <AIBlogGenerationDashboard />
          </ProviderWrapper>
        ),
      },
      {
        path: 'admin-panel/featured-referral',
        element: (
          <ProviderWrapper>
            <FeaturedReferralPage />
          </ProviderWrapper>
        ),
      },
      {
        path: 'admin-panel/theme-showcase',
        element: (
          <ProviderWrapper>
            <ThemeShowcasePage />
          </ProviderWrapper>
        ),
      },
      {
        path: 'admin-panel/cover-letter',
        element: (
          <ProviderWrapper>
            <CoverLetterGeneratorPage />
          </ProviderWrapper>
        ),
      },
      {
        path: 'analytics',
        element: (
          <ProviderWrapper>
            <AnalyticsDashboardContainer />
          </ProviderWrapper>
        ),
      },
      {
        path: 'claim/:groupReferralId',
        element: (
          <ProviderWrapper>
            <SiteReferralContainer />
          </ProviderWrapper>
        ),
      },
      {
        path: 'blog',
        element: (
          <ProviderWrapper>
            <BlogPostsContainer />
          </ProviderWrapper>
        ),
      },
      {
        path: 'blog/featured',
        element: (
          <ProviderWrapper>
            <BlogPostsContainer />
          </ProviderWrapper>
        ),
      },
      {
        // Hard-coded, code-driven technical post with live color demos.
        // Must precede 'blog/:slug' so the static segment wins.
        path: 'blog/color',
        element: (
          <ProviderWrapper>
            <ColorTheoryBlogPost />
          </ProviderWrapper>
        ),
      },
      {
        // Hard-coded, code-driven technical post with the live theme-showcase demo.
        // Must precede 'blog/:slug' so the static segment wins.
        path: 'blog/responsiveness',
        element: (
          <ProviderWrapper>
            <ResponsivenessBlogPost />
          </ProviderWrapper>
        ),
      },
      {
        // Hard-coded, code-driven technical post on the AI pipelines.
        // Must precede 'blog/:slug' so the static segment wins.
        path: 'blog/automation',
        element: (
          <ProviderWrapper>
            <AutomationBlogPost />
          </ProviderWrapper>
        ),
      },
      {
        path: 'blog/:slug',
        element: (
          <ProviderWrapper>
            <BlogPostContainer />
          </ProviderWrapper>
        ),
      },
      {
        path: 'store',
        element: (
          <ProviderWrapper includeStoreProvider>
            <StoreContainer />
          </ProviderWrapper>
        ),
      },
      {
        path: 'loading',
        element: (
          <ProviderWrapper>
            <AppLoader />
          </ProviderWrapper>
        ),
      },
      {
        path: 'test',
        element: (
          <ProviderWrapper includeStoreProvider>
            <BlankStoreContainer />
          </ProviderWrapper>
        ),
      },
      {
        path: 'blank',
        element: (
          <ProviderWrapper includeStoreProvider>
            <BGTesterContainer />
          </ProviderWrapper>
        ),
      },
      {
        path: 'palette-tester',
        element: (
          <ProviderWrapper includeStoreProvider>
            <PaletteTester />
          </ProviderWrapper>
        ),
      },
      {
        path: 'featured',
        element: <Navigate to="/" replace />,
      },
      {
        path: 'feedback',
        element: (
          <ProviderWrapper>
            <FeedbackContainer />
          </ProviderWrapper>
        ),
      },
      {
        path: 'tip',
        element: (
          <ProviderWrapper>
            <TipContainer />
          </ProviderWrapper>
        ),
      },
      {
        path: 'discounts',
        element: (
          <ProviderWrapper>
            <DiscountContainer />
          </ProviderWrapper>
        ),
      },
      {
        path: 'contact',
        element: (
          <ProviderWrapper>
            <ContactContainer />
          </ProviderWrapper>
        ),
      },
      {
        path: 'cody-snyder',
        element: (
          <ProviderWrapper>
            <AboutContainer />
          </ProviderWrapper>
        ),
      },
      {
        path: 'cody-s-snyder',
        element: (
          <ProviderWrapper>
            <AboutContainer />
          </ProviderWrapper>
        ),
      },
      {
        path: 'cody-the-coder',
        element: (
          <ProviderWrapper>
            <AboutContainer />
          </ProviderWrapper>
        ),
      },
      {
        path: 'about-me',
        element: (
          <ProviderWrapper>
            <AboutContainer />
          </ProviderWrapper>
        ),
      },
      {
        path: 'terms',
        element: (
          <ProviderWrapper>
            <TermsContainer />
          </ProviderWrapper>
        ),
      },
      {
        path: 'privacy',
        element: (
          <ProviderWrapper>
            <PrivacyContainer />
          </ProviderWrapper>
        ),
      },
      {
        path: 'search',
        element: (
          <ProviderWrapper>
            <SearchContainer />
          </ProviderWrapper>
        ),
      },
      {
        path: 'page/:pageId',
        element: (
          <ProviderWrapper includePageProviders>
            <PageContainer />
          </ProviderWrapper>
        ),
      },

      {
        path: 'page/:pageId/:referralSlug',
        element: (
          <ProviderWrapper includePageProviders>
            <GroupReferralSlugContainer />
          </ProviderWrapper>
        ),
      },
      // {
      //   path: "page/:pageId/:groupReferralId",
      //   element: (
      //     <ProviderWrapper includePageProviders>
      //       <GroupReferralContainer />
      //     </ProviderWrapper>
      //   )
      // },
      {
        path: 'user/:userId',
        element: (
          <ProviderWrapper>
            <UserContainer />
          </ProviderWrapper>
        ),
      },
      {
        path: 'referral/:referralId',
        element: (
          <ProviderWrapper>
            <ReferralContainer />
          </ProviderWrapper>
        ),
      },
      {
        path: '/:pageName/:referralSlug',
        element: (
          <ProviderWrapper includePageProviders>
            <GroupReferralSlugContainer />
          </ProviderWrapper>
        ),
      },
      {
        path: '/:pageName',
        element: (
          <ProviderWrapper includePageProviders>
            <PageContainer />
          </ProviderWrapper>
        ),
      },
    ],
  },
]);

const App = () => {
  return <RouterProvider router={router} />;
};

export default App;
